/**
 * City center coordinates and approximate neighborhood coordinates
 * for the interactive map on the Locations page.
 */

export interface CityCoords {
  lat: number;
  lng: number;
  zoom: number;
}

export const CITY_CENTERS: Record<string, CityCoords> = {
  miami:        { lat: 25.7617, lng: -80.1918, zoom: 12 },
  "new york":   { lat: 40.7128, lng: -74.0060, zoom: 12 },
  "los angeles":{ lat: 34.0522, lng: -118.2437, zoom: 11 },
  chicago:      { lat: 41.8781, lng: -87.6298, zoom: 12 },
  houston:      { lat: 29.7604, lng: -95.3698, zoom: 12 },
  austin:       { lat: 30.2672, lng: -97.7431, zoom: 12 },
  nashville:    { lat: 36.1627, lng: -86.7816, zoom: 12 },
  "new orleans":{ lat: 29.9511, lng: -90.0715, zoom: 12 },
  "san francisco":{ lat: 37.7749, lng: -122.4194, zoom: 12 },
  "las vegas":  { lat: 36.1699, lng: -115.1398, zoom: 12 },
  atlanta:      { lat: 33.7490, lng: -84.3880, zoom: 12 },
  london:       { lat: 51.5074, lng: -0.1278, zoom: 12 },
  manchester:   { lat: 53.4808, lng: -2.2426, zoom: 13 },
  edinburgh:    { lat: 55.9533, lng: -3.1883, zoom: 13 },
  madrid:       { lat: 40.4168, lng: -3.7038, zoom: 13 },
  barcelona:    { lat: 41.3851, lng: 2.1734, zoom: 13 },
  milan:        { lat: 45.4654, lng: 9.1859, zoom: 13 },
  rome:         { lat: 41.9028, lng: 12.4964, zoom: 13 },
  florence:     { lat: 43.7696, lng: 11.2558, zoom: 14 },
  paris:        { lat: 48.8566, lng: 2.3522, zoom: 12 },
  amsterdam:    { lat: 52.3676, lng: 4.9041, zoom: 13 },
  berlin:       { lat: 52.5200, lng: 13.4050, zoom: 12 },
  lisbon:       { lat: 38.7223, lng: -9.1393, zoom: 13 },
  sydney:       { lat: -33.8688, lng: 151.2093, zoom: 12 },
  melbourne:    { lat: -37.8136, lng: 144.9631, zoom: 12 },
  brisbane:     { lat: -27.4705, lng: 153.0260, zoom: 12 },
  dubai:        { lat: 25.2048, lng: 55.2708, zoom: 12 },
  "abu dhabi":  { lat: 24.4539, lng: 54.3773, zoom: 12 },
  toronto:      { lat: 43.6532, lng: -79.3832, zoom: 12 },
  vancouver:    { lat: 49.2827, lng: -123.1207, zoom: 12 },
  singapore:    { lat: 1.3521, lng: 103.8198, zoom: 12 },
  "são paulo":  { lat: -23.5505, lng: -46.6333, zoom: 12 },
  "sao paulo":  { lat: -23.5505, lng: -46.6333, zoom: 12 },
  tokyo:        { lat: 35.6762, lng: 139.6503, zoom: 12 },
  "hong kong":  { lat: 22.3193, lng: 114.1694, zoom: 12 },
  bangkok:      { lat: 13.7563, lng: 100.5018, zoom: 12 },
  shanghai:     { lat: 31.2304, lng: 121.4737, zoom: 12 },
  "mexico city":{ lat: 19.4326, lng: -99.1332, zoom: 12 },
  "buenos aires":{ lat: -34.6037, lng: -58.3816, zoom: 12 },
  bogota:       { lat: 4.7110, lng: -74.0721, zoom: 12 },
  "bogotá":     { lat: 4.7110, lng: -74.0721, zoom: 12 },
};

/** Approximate neighborhood coordinates keyed by "city::neighborhood" */
export const NEIGHBORHOOD_COORDS: Record<string, [number, number]> = {
  // Miami
  "miami::Brickell":       [25.7617, -80.1918],
  "miami::Wynwood":        [25.8010, -80.1990],
  "miami::Little Havana":  [25.7660, -80.2220],
  "miami::Kendall":        [25.6800, -80.3110],
  // New York
  "new york::East Village":       [40.7265, -73.9815],
  "new york::Williamsburg":       [40.7081, -73.9571],
  "new york::Astoria":            [40.7720, -73.9301],
  "new york::Hell's Kitchen":     [40.7638, -73.9918],
  "new york::Long Island City":   [40.7447, -73.9485],
  // Los Angeles
  "los angeles::Silver Lake":     [34.0869, -118.2702],
  "los angeles::Highland Park":   [34.1122, -118.1928],
  "los angeles::Culver City":     [34.0211, -118.3965],
  "los angeles::Echo Park":       [34.0782, -118.2606],
  // Chicago
  "chicago::Wicker Park":   [41.9088, -87.6796],
  "chicago::Logan Square":  [41.9234, -87.7084],
  "chicago::River North":   [41.8917, -87.6318],
  "chicago::Pilsen":        [41.8562, -87.6564],
  // Houston
  "houston::Montrose":      [29.7440, -95.3910],
  "houston::EaDo (East Downtown)": [29.7490, -95.3520],
  "houston::The Heights":   [29.7910, -95.3950],
  "houston::Midtown":       [29.7430, -95.3760],
  // Austin
  "austin::South Congress (SoCo)": [30.2470, -97.7490],
  "austin::East Austin":           [30.2620, -97.7250],
  "austin::The Domain":            [30.4020, -97.7250],
  "austin::North Loop":            [30.3180, -97.7220],
  // Nashville
  "nashville::The Gulch":     [36.1510, -86.7890],
  "nashville::12South":       [36.1260, -86.7930],
  "nashville::East Nashville":[36.1750, -86.7540],
  "nashville::Germantown":    [36.1780, -86.7840],
  // London
  "london::Shoreditch":          [51.5264, -0.0780],
  "london::Peckham":             [51.4740, -0.0690],
  "london::Brixton":             [51.4613, -0.1156],
  "london::Soho":                [51.5137, -0.1335],
  "london::Borough Market Area": [51.5055, -0.0910],
  // Manchester
  "manchester::Ancoats":          [53.4860, -2.2290],
  "manchester::Northern Quarter": [53.4840, -2.2370],
  "manchester::Didsbury":         [53.4170, -2.2300],
  "manchester::Deansgate":        [53.4760, -2.2500],
  // Edinburgh
  "edinburgh::Leith":        [55.9760, -3.1720],
  "edinburgh::Stockbridge":  [55.9580, -3.2100],
  "edinburgh::Grassmarket":  [55.9480, -3.1960],
  "edinburgh::Bruntsfield":  [55.9400, -3.2060],
};

export function getCityCenter(city: string): CityCoords | null {
  const key = city.toLowerCase().trim();
  if (CITY_CENTERS[key]) return CITY_CENTERS[key];
  for (const [k, v] of Object.entries(CITY_CENTERS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return null;
}

export function getNeighborhoodCoords(city: string, neighborhoodName: string): [number, number] | null {
  const key = city.toLowerCase().trim();
  // Try direct match
  const direct = NEIGHBORHOOD_COORDS[`${key}::${neighborhoodName}`];
  if (direct) return direct;
  // Try partial city match
  for (const [k, v] of Object.entries(NEIGHBORHOOD_COORDS)) {
    const [c, n] = k.split("::");
    if ((key.includes(c) || c.includes(key)) && n === neighborhoodName) return v;
  }
  return null;
}
